/**
 * ReputationTimeline — Guest-facing timeline of SBT score events.
 * Renders on GuestPortalPage when the guest has recorded events.
 */
import { useEffect, useState } from 'react';
import { Loader2, TrendingUp, ShieldAlert, Flame, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BACKEND_URL } from '@/lib/backendUrl';

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ReputationTimeline({ email, wallet }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!email && !wallet) { setLoading(false); return; }
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (email) params.set('email', email);
        if (wallet) params.set('wallet', wallet);
        const r = await fetch(`${BACKEND_URL}/api/reputation/timeline?${params}`);
        if (r.ok && !cancelled) setData(await r.json());
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [email, wallet]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6" data-testid="timeline-loading">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || data.count === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-5" data-testid="timeline-empty">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm">Reputation timeline</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Your SBT events will appear here after your first hotel check-in.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="reputation-timeline">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Reputation timeline</h3>
        </div>
        <div className="flex items-center gap-2">
          {data.current_streak > 0 && (
            <Badge className="bg-amber-500/10 text-amber-600 border border-amber-500/20 text-[10px]" data-testid="timeline-streak">
              <Flame className="w-3 h-3 mr-1" /> {data.current_streak}-stay streak
            </Badge>
          )}
          <Badge className="bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 text-[10px]" data-testid="timeline-score">
            <Sparkles className="w-3 h-3 mr-1" /> {data.current_score ?? 0} pts
          </Badge>
        </div>
      </div>

      <div className="space-y-2.5">
        {data.events.map((ev) => {
          const isPositive = ev.delta > 0;
          return (
            <div
              key={ev.id}
              className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0"
              data-testid={`timeline-event-${ev.event_type}`}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  isPositive ? 'bg-emerald-500/10' : 'bg-red-500/10'
                }`}
              >
                {isPositive ? (
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                ) : (
                  <ShieldAlert className="w-4 h-4 text-red-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium truncate">
                    {ev.event_type === 'clean_checkin' && `Clean check-in${ev.hotel_name ? ` at ${ev.hotel_name}` : ''}`}
                    {ev.event_type === 'identity_mismatch' && 'Identity mismatch flagged'}
                  </p>
                  <span
                    className={`text-sm font-mono font-semibold shrink-0 ${
                      isPositive ? 'text-emerald-600' : 'text-red-500'
                    }`}
                  >
                    {isPositive ? '+' : ''}{ev.delta}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
                  <span>{relativeTime(ev.created_at)}</span>
                  {ev.streak_bonus > 0 && (
                    <>
                      <span>·</span>
                      <span className="text-amber-600 font-medium">
                        +{ev.streak_bonus} streak bonus · {ev.streak_after}-stay streak
                      </span>
                    </>
                  )}
                  {ev.score_after != null && (
                    <>
                      <span>·</span>
                      <span>→ {ev.score_after} pts</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-muted-foreground mt-4 text-center">
        Your reputation travels across getbitsy hotels. Keep it clean.
      </p>
    </div>
  );
}
